import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../ui/Modal';
import { adminClientsService } from '../../services/adminClientsService';
import { toastSuccess, toastError } from '../../lib/toast';

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SLUG_REGEX = /^[a-z0-9-]+$/;

export const CreateClientModal: React.FC<CreateClientModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [storageBackend, setStorageBackend] = useState('minio_local');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; slug?: string }>({});

  const reset = () => {
    setName('');
    setSlug('');
    setStorageBackend('minio_local');
    setErrors({});
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const validate = (): boolean => {
    const next: { name?: string; slug?: string } = {};
    if (!name.trim()) next.name = 'Nom requis';
    if (!slug.trim()) {
      next.slug = 'Slug requis';
    } else if (!SLUG_REGEX.test(slug)) {
      next.slug = 'Lettres minuscules, chiffres et tirets uniquement';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const created = await adminClientsService.createClient({
        name: name.trim(),
        slug: slug.trim(),
        storage_backend: storageBackend,
      });
      toastSuccess('Client créé');
      reset();
      onClose();
      navigate(`/parametres/clients/${created.id}`);
    } catch (err: any) {
      const msg = err?.message || '';
      if (/déjà|exist|409|conflict/i.test(msg) && /slug/i.test(msg + slug)) {
        toastError('Ce slug est déjà utilisé');
      } else if (/409/i.test(msg)) {
        toastError('Ce slug est déjà utilisé');
      } else {
        toastError('Création échouée', err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Nouveau client" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="Acme Notaires"
            autoFocus
          />
          {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            placeholder="acme-notaires"
          />
          <p className="text-xs text-gray-500 mt-1">
            Identifiant URL. Lettres minuscules, chiffres et tirets uniquement. Non modifiable après création.
          </p>
          {errors.slug && <p className="text-xs text-red-600 mt-1">{errors.slug}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Stockage</label>
          <select
            value={storageBackend}
            onChange={(e) => setStorageBackend(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          >
            <option value="minio_local">MinIO local</option>
            <option value="s3_remote">S3 distant</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
          >
            {submitting ? 'Création...' : 'Créer'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateClientModal;
